import { createSlice, createSelector, type PayloadAction } from '@reduxjs/toolkit';
import type { RootState } from '..';

/**
 * Represents a selected feature together with the layer it belongs to.
 *
 * Features from multiple layers can share the same raw `featureId`, so the
 * combination of `layerId` and `featureId` is used as the canonical key.
 */
export interface SelectedFeature {
  /** ID of the feature within its source layer */
  featureId: string;
  /** ID of the layer that owns the feature */
  layerId: string;
  /** Optional snapshot of the feature's attribute properties */
  properties?: Record<string, unknown>;
}

/**
 * How the current selection was made. Useful for UI components to adjust
 * behaviour (e.g. render a box-selection overlay) without inspecting
 * interaction state directly.
 */
export type SelectionMode = 'idle' | 'click' | 'box' | 'shift-click';

export interface SelectionState {
  /**
   * Full records for every currently-selected feature. The order of this
   * array reflects the order in which features were added to the selection.
   */
  selectedFeatures: SelectedFeature[];
  /** The interaction mode that produced the current selection. */
  mode: SelectionMode;
}

const initialState: SelectionState = {
  selectedFeatures: [],
  mode: 'idle',
};

/** Canonical string key used to identify a selected feature. */
export const selectionKey = (layerId: string, featureId: string): string =>
  `${layerId}::${featureId}`;

const featureKey = (feature: SelectedFeature): string =>
  selectionKey(feature.layerId, feature.featureId);

/** De-duplicate an array of features while preserving first-seen order. */
const dedupeFeatures = (features: SelectedFeature[]): SelectedFeature[] => {
  const seen = new Set<string>();
  const result: SelectedFeature[] = [];
  for (const feature of features) {
    const key = featureKey(feature);
    if (!seen.has(key)) {
      seen.add(key);
      result.push(feature);
    }
  }
  return result;
};

/**
 * Redux slice for feature selection state.
 *
 * Manages which features are currently selected on the map and provides the
 * foundation for click, box, and shift-click selection interactions along
 * with clear-selection and selection-count UI features.
 *
 * **Validates: Requirement 11 - Feature Selection**
 *   - AC 1: click-to-select a feature and highlight it on the map
 *   - AC 2: draw a selection box to select all features within the box
 *   - AC 3: shift-click adds features to the current selection
 *   - AC 4: click on empty space clears the current selection
 *   - AC 5: display the count of selected features
 */
const selectionSlice = createSlice({
  name: 'selection',
  initialState,
  reducers: {
    /**
     * Replace the current selection with a single feature (click-to-select).
     *
     * AC 1: WHEN a Registered_User clicks on a Feature, THE Feature_Selector
     *       SHALL select the feature and highlight it on the map.
     */
    selectFeature: (state, action: PayloadAction<SelectedFeature>) => {
      state.selectedFeatures = [action.payload];
      state.mode = 'click';
    },

    /**
     * Add a feature to the current selection (shift-click multi-select).
     * No-op when the feature is already selected.
     *
     * AC 3: WHEN a Registered_User holds the shift key and selects features,
     *       THE Feature_Selector SHALL add features to the current selection.
     */
    addToSelection: (state, action: PayloadAction<SelectedFeature>) => {
      const key = featureKey(action.payload);
      const exists = state.selectedFeatures.some((f) => featureKey(f) === key);
      if (!exists) {
        state.selectedFeatures.push(action.payload);
      }
      state.mode = 'shift-click';
    },

    /**
     * Add many features to the selection in one step, typically used by the
     * box-selection interaction. Duplicates (within the payload or against
     * the existing selection) are dropped.
     *
     * AC 2: WHEN a Registered_User draws a selection box, THE Feature_Selector
     *       SHALL select all features within the box.
     */
    addManyToSelection: (state, action: PayloadAction<SelectedFeature[]>) => {
      const existingKeys = new Set(state.selectedFeatures.map(featureKey));
      for (const feature of action.payload) {
        const key = featureKey(feature);
        if (!existingKeys.has(key)) {
          existingKeys.add(key);
          state.selectedFeatures.push(feature);
        }
      }
      state.mode = 'box';
    },

    /**
     * Remove a feature from the current selection. Used to toggle a feature
     * off with shift-click when it is already selected.
     */
    removeFromSelection: (state, action: PayloadAction<{ layerId: string; featureId: string }>) => {
      const key = selectionKey(action.payload.layerId, action.payload.featureId);
      state.selectedFeatures = state.selectedFeatures.filter((f) => featureKey(f) !== key);
      if (state.selectedFeatures.length === 0) {
        state.mode = 'idle';
      }
    },

    /**
     * Replace the entire selection with a new set of features. Useful when
     * a box selection computes the final list of features and wants to
     * overwrite any previous selection, rather than adding to it.
     */
    setSelection: (state, action: PayloadAction<SelectedFeature[]>) => {
      state.selectedFeatures = dedupeFeatures(action.payload);
      state.mode = state.selectedFeatures.length > 0 ? 'box' : 'idle';
    },

    /**
     * Clear the current selection entirely.
     *
     * AC 4: WHEN a Registered_User clicks on empty space, THE Feature_Selector
     *       SHALL clear the current selection.
     */
    clearSelection: (state) => {
      state.selectedFeatures = [];
      state.mode = 'idle';
    },

    /** Explicitly set the current selection mode. */
    setSelectionMode: (state, action: PayloadAction<SelectionMode>) => {
      state.mode = action.payload;
    },
  },
});

export const {
  selectFeature,
  addToSelection,
  addManyToSelection,
  removeFromSelection,
  setSelection,
  clearSelection,
  setSelectionMode,
} = selectionSlice.actions;

/**
 * Backwards-compatible alias for {@link addToSelection}. Prefer
 * `addToSelection` in new code.
 */
export const addFeatureToSelection = addToSelection;

/**
 * Alias for {@link setSelection}. Replaces the current selection with the
 * provided list of features. Matches the task-specified API name.
 */
export const selectFeatures = setSelection;

export default selectionSlice.reducer;

/* -------------------------------------------------------------------------- */
/*  Selectors                                                                 */
/* -------------------------------------------------------------------------- */

/** Raw slice state. */
const selectSelectionState = (state: RootState): SelectionState => state.selection;

/** All currently-selected features. */
export const selectSelectedFeatures = createSelector(
  selectSelectionState,
  (selection) => selection.selectedFeatures
);

/** IDs of currently-selected features (in selection order). */
export const selectSelectedFeatureIds = createSelector(selectSelectedFeatures, (features) =>
  features.map((f) => f.featureId)
);

/**
 * The layer id associated with the selection. When features from multiple
 * layers are selected this returns `null`. When a single layer is involved
 * it returns that layer id.
 */
export const selectSelectedLayerId = createSelector(
  selectSelectedFeatures,
  (features): string | null => {
    if (features.length === 0) return null;
    const firstLayerId = features[0].layerId;
    return features.every((f) => f.layerId === firstLayerId) ? firstLayerId : null;
  }
);

/**
 * Number of currently-selected features.
 *
 * AC 5: THE Feature_Selector SHALL display the count of selected features.
 */
export const selectSelectedCount = createSelector(
  selectSelectedFeatures,
  (features) => features.length
);

/** Whether any feature is currently selected. */
export const selectHasSelection = createSelector(selectSelectedCount, (count) => count > 0);

/** Current selection mode. */
export const selectSelectionMode = createSelector(
  selectSelectionState,
  (selection) => selection.mode
);

/**
 * Factory for a selector that reports whether a given feature is selected.
 * The returned selector is memoized for its (layerId, featureId) pair.
 */
export const selectIsFeatureSelected = (layerId: string, featureId: string) =>
  createSelector(selectSelectedFeatures, (features) =>
    features.some((f) => f.layerId === layerId && f.featureId === featureId)
  );

/* -------------------------------------------------------------------------- */
/*  Task-specified selector aliases                                           */
/* -------------------------------------------------------------------------- */

/**
 * Alias for {@link selectSelectedFeatureIds}.
 * Matches the task-specified API name `getSelectedFeatureIds`.
 */
export const getSelectedFeatureIds = selectSelectedFeatureIds;

/**
 * Alias for {@link selectSelectedCount}.
 * Matches the task-specified API name `getSelectedFeatureCount`.
 */
export const getSelectedFeatureCount = selectSelectedCount;

/**
 * Alias for {@link selectSelectionMode}.
 * Matches the task-specified API name `getSelectionMode`.
 */
export const getSelectionMode = selectSelectionMode;

/**
 * Alias for {@link selectIsFeatureSelected}.
 * Matches the task-specified API name `isFeatureSelected`.
 */
export const isFeatureSelected = selectIsFeatureSelected;
